import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

import { TaskWithAssignments } from '@scrambl/shared'

// UI State only
interface TaskState {
  // Selected task management (UI state)
  selectedTask: TaskWithAssignments | null

  // Multi-selection state (UI state)
  selectedTaskIds: Set<string>
  isSelectionMode: boolean

  // Name editing state (UI state)
  editingTaskId: string | null

  // Task selection actions
  setSelectedTask: (task: TaskWithAssignments | null) => void

  // Multi-selection actions
  toggleTaskSelection: (taskId: string) => void
  selectAllTasks: (taskIds: string[]) => void
  addToSelection: (taskIds: string[]) => void
  removeFromSelection: (taskIds: string[]) => void
  clearSelection: () => void
  toggleSelectionMode: () => void
  setSelectionMode: (enabled: boolean) => void
  isTaskSelected: (taskId: string) => boolean
  getSelectedTasks: (allTasks: TaskWithAssignments[]) => TaskWithAssignments[]
  getSelectedCount: () => number

  // Shared selection source tracking (for sprint cross-table selection)
  lastSelectionSourceId: string | null
  setLastSelectionSourceId: (id: string | null) => void

  // Name editing actions
  setEditingTask: (taskId: string | null) => void
  isEditing: (taskId: string) => boolean

  // UI helper - updates selected task when task data changes (for optimistic updates)
  updateTaskInStore: (
    id: string,
    data: Partial<TaskWithAssignments>,
    options?: { skipEvent?: boolean },
  ) => void

  // Reset actions
  reset: () => void
}

/**
 * Simplified TaskState that only manages UI state
 * Server state (fetching, creating, updating, deleting tasks)
 * is managed by TanStack Query hooks
 */
export const useTaskState = create<TaskState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    selectedTask: null,
    selectedTaskIds: new Set<string>(),
    isSelectionMode: false,
    editingTaskId: null,
    lastSelectionSourceId: null,

    // Task selection actions
    setSelectedTask: task => {
      set({ selectedTask: task })
    },

    // Multi-selection actions
    toggleTaskSelection: (taskId: string) => {
      set(state => {
        const newSelectedIds = new Set(state.selectedTaskIds)
        if (newSelectedIds.has(taskId)) {
          newSelectedIds.delete(taskId)
        } else {
          newSelectedIds.add(taskId)
        }

        // If no tasks are selected, exit selection mode
        if (newSelectedIds.size === 0) {
          return {
            selectedTaskIds: newSelectedIds,
            isSelectionMode: false,
          }
        }

        // If tasks are selected, enter selection mode
        return {
          selectedTaskIds: newSelectedIds,
          isSelectionMode: true,
        }
      })
    },

    selectAllTasks: (taskIds: string[]) => {
      set({
        selectedTaskIds: new Set(taskIds),
        isSelectionMode: taskIds.length > 0,
      })
    },

    addToSelection: (taskIds: string[]) => {
      if (taskIds.length === 0) return
      set(state => {
        const newSet = new Set(state.selectedTaskIds)
        taskIds.forEach(id => newSet.add(id))
        return { selectedTaskIds: newSet, isSelectionMode: true }
      })
    },

    removeFromSelection: (taskIds: string[]) => {
      if (taskIds.length === 0) return
      set(state => {
        const newSet = new Set(state.selectedTaskIds)
        taskIds.forEach(id => newSet.delete(id))
        return {
          selectedTaskIds: newSet,
          isSelectionMode: newSet.size > 0,
        }
      })
    },

    clearSelection: () => {
      set({
        selectedTaskIds: new Set<string>(),
        isSelectionMode: false,
        lastSelectionSourceId: null,
      })
    },

    toggleSelectionMode: () => {
      set(state => ({
        isSelectionMode: !state.isSelectionMode,
        selectedTaskIds: state.isSelectionMode
          ? new Set<string>()
          : state.selectedTaskIds,
      }))
    },

    setSelectionMode: (enabled: boolean) => {
      set({
        isSelectionMode: enabled,
        selectedTaskIds: enabled ? get().selectedTaskIds : new Set<string>(),
      })
    },

    isTaskSelected: (taskId: string) => {
      return get().selectedTaskIds.has(taskId)
    },

    getSelectedTasks: (allTasks: TaskWithAssignments[]) => {
      const selectedIds = get().selectedTaskIds
      return allTasks.filter(task => selectedIds.has(task.id))
    },

    getSelectedCount: () => {
      return get().selectedTaskIds.size
    },

    // Shared selection source tracking
    setLastSelectionSourceId: (id: string | null) => {
      set({ lastSelectionSourceId: id })
    },

    // Name editing actions
    setEditingTask: (taskId: string | null) => {
      set({ editingTaskId: taskId })
    },

    isEditing: (taskId: string) => {
      return get().editingTaskId === taskId
    },

    // UI helper - updates selected task in store when task data changes
    // This is for optimistic updates or when task data changes externally
    updateTaskInStore: (
      id: string,
      data: Partial<TaskWithAssignments>,
      options?: { skipEvent?: boolean },
    ) => {
      const state = get()

      // Update selected task if it matches
      if (state.selectedTask?.id === id) {
        const nextTask = {
          ...state.selectedTask,
          ...data,
        } as TaskWithAssignments
        if (Object.prototype.hasOwnProperty.call(data, 'groupId')) {
          const nextGroupId = data.groupId ?? null
          nextTask.group =
            data.group && data.group.id === nextGroupId
              ? data.group
              : state.selectedTask.group?.id === nextGroupId
                ? state.selectedTask.group
                : null
        }
        set({ selectedTask: nextTask })
      }

      // Dispatch event if not skipped (for cross-component communication)
      if (!options?.skipEvent) {
        window.dispatchEvent(
          new CustomEvent('taskUpdated', {
            detail: { taskId: id, updates: data, timestamp: Date.now() },
          }),
        )
      }
    },

    reset: () => {
      set({
        selectedTask: null,
        selectedTaskIds: new Set<string>(),
        isSelectionMode: false,
        editingTaskId: null,
        lastSelectionSourceId: null,
      })
    },
  })),
)

export type { TaskState }
